import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { RotaCheckbox, RotaButton, useToast } from "@teamrota/rota-design";
import { POOL_TYPES } from "@teamrota/rota-common";
import { useMemberClients } from "./graphql/get-member-clients";
import { useMemberRelationships } from "./graphql/get-member-relationships";
import useRelationships from "./graphql/manage-relationships";
import {
  Container,
  Content,
  PartnerSection,
  PartnerName,
  ClientList,
  ClientItem,
  NoClients,
  BlacklistedLabel,
  ActionButtonContainer
} from "./styles";
import { useMemberBookings } from "./graphql/get-member-bookings";

const MemberClients = ({ memberId }) => {
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);

  const {
    clientAccounts,
    loading: clientAccountsLoading,
    error: clientAccountsError
  } = useMemberClients(memberId);
  const {
    relationships,
    loading: relationshipsLoading,
    error: relationshipsError,
    refetch: refetchRelationships
  } = useMemberRelationships(memberId);

  const { createOrUpdateRelationship } = useRelationships();
  const { checkClientBookings } = useMemberBookings(memberId);
  const { showToast } = useToast();

  useEffect(() => {
    const isDataReady =
      !clientAccountsLoading &&
      !relationshipsLoading &&
      clientAccounts &&
      relationships;
    const needsInitialization = !initializedRef.current && isDataReady;

    if (needsInitialization) {
      const blacklistedIds = new Set();

      for (let i = 0; i < relationships.length; i++) {
        const rel = relationships[i];
        if (rel.poolType === POOL_TYPES.BLACKLISTED) {
          blacklistedIds.add(rel.account.id);
        }
      }

      const initialSelected = new Set();

      for (let i = 0; i < clientAccounts.length; i++) {
        const client = clientAccounts[i];
        if (!blacklistedIds.has(client.id)) {
          initialSelected.add(client.id);
        }
      }

      setSelectedClientIds(initialSelected);
      initializedRef.current = true;
    }
  }, [clientAccountsLoading, relationshipsLoading]);

  useEffect(() => {
    if (initializedRef.current) {
      initializedRef.current = false;
    }
  }, [refetchRelationships]);

  const loading = clientAccountsLoading || relationshipsLoading;
  const error = clientAccountsError || relationshipsError;

  if (loading) return <div>Loading client accounts...</div>;
  if (error) return <div>Error loading client accounts</div>;

  if (!clientAccounts?.length) {
    return <NoClients>No client accounts found</NoClients>;
  }

  const blacklistedClientIds = new Set(
    relationships
      .filter(rel => rel.poolType === POOL_TYPES.BLACKLISTED)
      .map(rel => rel.account.id)
  );

  const handleClientToggle = clientId => {
    setSelectedClientIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(clientId)) {
        newSelected.delete(clientId);
      } else {
        newSelected.add(clientId);
      }
      return newSelected;
    });
  };

  const sortedClients = [...clientAccounts].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const clientsNeedingBlacklist = clientAccounts.filter(
        client =>
          !selectedClientIds.has(client.id) &&
          !blacklistedClientIds.has(client.id)
      );

      const clientsNeedingRemoval = clientAccounts.filter(
        client =>
          selectedClientIds.has(client.id) &&
          blacklistedClientIds.has(client.id)
      );

      const clientsWithUpcomingBookings = [];

      for (const client of clientsNeedingBlacklist) {
        const { data } = await checkClientBookings(client.id);

        const upcomingBookings =
          data?.account?.member?.bookingsCount?.upcomingAccepted || 0;

        if (upcomingBookings > 0) {
          clientsWithUpcomingBookings.push(client);
        }
      }

      // If there are clients with upcoming bookings, show error and stop
      if (clientsWithUpcomingBookings.length > 0) {
        const clientNames = clientsWithUpcomingBookings
          .map(client => client.name)
          .join(", ");
        showToast(
          `You cannot save your changes as you have got future shifts for ${clientNames}. You need to cancel these shifts before you can proceed.`,
          { severity: "error" }
        );
        return;
      }

      for (const client of clientsNeedingBlacklist) {
        await createOrUpdateRelationship({
          variables: {
            memberId,
            poolType: POOL_TYPES.BLACKLISTED,
            accountId: client.id
          }
        });
      }

      for (const client of clientsNeedingRemoval) {
        await createOrUpdateRelationship({
          variables: {
            memberId,
            poolType: POOL_TYPES.RECOMMENDED,
            accountId: client.id
          }
        });
      }

      await refetchRelationships();

      showToast("Changes saved successfully.", { severity: "success" });
      initializedRef.current = false;
    } catch (error) {
      console.error("Error saving client relationships:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <Content>
        <PartnerSection>
          <PartnerName>Clients</PartnerName>
          <ClientList>
            {sortedClients.map(client => (
              <ClientItem key={client.id}>
                <RotaCheckbox
                  isChecked={selectedClientIds.has(client.id)}
                  onClick={() => handleClientToggle(client.id)}
                />
                <span>{client.name}</span>
                {blacklistedClientIds.has(client.id) && (
                  <BlacklistedLabel>(Blacklisted)</BlacklistedLabel>
                )}
              </ClientItem>
            ))}
          </ClientList>
        </PartnerSection>
      </Content>
      <ActionButtonContainer>
        <RotaButton
          onClick={handleSave}
          isLoading={isSaving}
          disabled={isSaving}
        >
          Save Changes
        </RotaButton>
      </ActionButtonContainer>
    </Container>
  );
};

MemberClients.propTypes = {
  memberId: PropTypes.string.isRequired
};

export default MemberClients;
